import { useState } from "react";
import { formatRelativeShort } from "@/design/format";
import { AppShell, stratusNav } from "@/shell/AppShell";
import {
  Stat,
  FilterChips,
  Toolbar,
  ToolbarSelect,
  SearchInput,
  DataTable,
  type Column,
  Pill,
  Button,
  ProgressBar,
  EmptyState,
} from "@/design/components";
import { useSeed, useOrgIndex } from "@/domain/hooks";
import type { KycReview } from "@/domain/types";
import { KycDetailDrawer } from "./KycDetailDrawer";

const FILTERS = [
  { value: "all", label: "All" },
  { value: "pending_review", label: "Pending review" },
  { value: "queried", label: "Queried" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export function KycReviewQueue() {
  const seed = useSeed();
  const orgs = useOrgIndex();
  const [filter, setFilter] = useState("all");
  const [tier, setTier] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const rows = seed.kycReviews.filter((k) => {
    if (filter !== "all" && k.state !== filter) return false;
    if (tier && k.tier !== tier) return false;
    if (q && !(orgs[k.organisationId]?.name.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  const open = seed.kycReviews.filter((k) => k.state === "pending_review" || k.state === "queried").length;

  const columns: Column<KycReview>[] = [
    {
      key: "org",
      header: "Organisation",
      cell: (k) => (
        <div>
          <p className="font-medium text-ink">{orgs[k.organisationId]?.name}</p>
          <Pill variant={k.tier === "prime" ? "brand" : "neutral"} className="mt-1">{k.tier === "prime" ? "Prime" : "Subcontractor"}</Pill>
        </div>
      ),
    },
    {
      key: "docs",
      header: "Documents",
      cell: (k) => {
        const req = k.requiredDocuments.filter((d) => d.required);
        const present = req.filter((d) => d.documentId).length;
        return (
          <div className="w-28">
            <span className="text-sm tabular-nums text-ink">{present} of {req.length}</span>
            <ProgressBar value={req.length ? present / req.length : 0} className="mt-1" />
          </div>
        );
      },
    },
    { key: "submitted", header: "Submitted", cell: (k) => <span className="text-xs text-faint">{formatRelativeShort(k.submittedAt)}</span> },
    {
      key: "status",
      header: "Status",
      cell: (k) => (
        <Pill variant={k.state === "approved" ? "success" : k.state === "queried" ? "warn" : k.state === "rejected" ? "danger" : "info"}>
          {label(k.state)}
        </Pill>
      ),
    },
    { key: "reviewer", header: "Reviewer", cell: (k) => <span className="text-mute">{k.reviewerId ? "David Kirabo" : "Unassigned"}</span> },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (k) => <Button size="sm" variant="secondary" onClick={() => setOpenId(k.id)}>Open</Button>,
    },
  ];

  const openReview = seed.kycReviews.find((k) => k.id === openId) ?? null;

  return (
    <AppShell
      context="Stratus Health"
      sections={stratusNav}
      crumbs={[{ label: "Stratus", to: "/stratus" }, { label: "Controls", to: "/stratus/controls/contractors" }, { label: "KYC reviews" }]}
    >
      <div className="px-4 py-5 md:px-8 md:py-6">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">KYC reviews</h1>
        <p className="mt-1 text-sm text-mute">In-house review of contractor documents. No third-party vendor.</p>

        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Open reviews" value={String(open)} />
          <Stat label="Average turnaround" value="19 hours" />
          <Stat label="Approved this week" value="4" />
          <Stat label="Queried this week" value="2" />
        </div>

        <div className="mt-6 space-y-4">
          <FilterChips options={FILTERS} value={filter} onChange={setFilter} />
          <Toolbar>
            <ToolbarSelect value={tier} onChange={setTier} placeholder="All tiers" options={[{ value: "prime", label: "Prime" }, { value: "sub", label: "Subcontractor" }]} />
            <SearchInput value={q} onChange={setQ} placeholder="Search organisations" className="w-64" />
          </Toolbar>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(k) => k.id}
            onRowClick={(k) => setOpenId(k.id)}
            empty={<EmptyState title="No KYC reviews waiting" body="New submissions land here as contractors complete the document upload step in onboarding." />}
          />
        </div>
      </div>

      <KycDetailDrawer review={openReview} open={!!openReview} onOpenChange={(o) => !o && setOpenId(null)} />
    </AppShell>
  );
}

function label(s: KycReview["state"]): string {
  return { pending_review: "Pending review", queried: "Queried", approved: "Approved", rejected: "Rejected" }[s];
}
